import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, Globe, Lock, Droplets, Route,
  Shield, ShieldAlert, Key, Rss, Target, TrendingUp,
  Radio, Crosshair, Cpu, Eye, Sparkles,
  Settings, HelpCircle, Plug, Wrench, Loader2,
  Activity, Server, FileText,
} from 'lucide-react';
import { useCompany } from '../shared/context/CompanyContext';
import { useAuth } from '../shared/context/AuthContext';
import { useActiveProduct } from '../shared/hooks/useActiveProduct';
import { SHELL_MAIN_ITEMS, SHELL_SYSTEM_ITEMS } from '../shared/config/productConfig';

/** Map icon name strings from productConfig → actual Lucide components */
const ICON_MAP = {
  LayoutDashboard, Globe, Lock, Droplets, Route,
  Shield, ShieldAlert, Key, Rss, Target, TrendingUp,
  Radio, Crosshair, Cpu, Eye, Sparkles,
  Settings, HelpCircle, Plug, Wrench,
  Activity, Server, FileText,
};

/** Section header label — teal, uppercase, used for MAIN / SURFACE / SYSTEM */
function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-2 px-3 pt-5 pb-1.5">
      <span className="text-[11px] font-bold uppercase tracking-widest text-teal-400">
        {children}
      </span>
    </div>
  );
}

/** Sub-section label within a product section — e.g. "Priority", "Analysis" */
function SubSectionLabel({ children }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-widest text-navy-500 px-3 pt-3 pb-0.5">
      {children}
    </p>
  );
}

/** Shared nav-link renderer — 20% bigger font, more vertical space */
function NavLink({ item, active, locked = false, activeColor }) {
  const Icon = ICON_MAP[item.icon] || Globe;
  return (
    <Link
      to={item.path}
      onClick={(e) => locked && e.preventDefault()}
      className={`flex items-center gap-3 px-3 py-2.5 text-[15px] rounded-lg transition-all ${
        active
          ? 'bg-navy-800 text-white border-l-4 pl-2'
          : locked
          ? 'text-gray-600 opacity-60 hover:bg-navy-800/50 cursor-not-allowed'
          : 'text-gray-300 hover:bg-navy-800 hover:text-white'
      }`}
      style={active && activeColor ? { borderLeftColor: activeColor } : undefined}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="truncate">{item.label}</span>
      {locked && <Lock className="w-3 h-3 ml-auto text-gray-600 flex-shrink-0" />}
    </Link>
  );
}

const ProductSidebar = () => {
  const location = useLocation();
  const activeProduct = useActiveProduct();
  const { activeCompany, scanning, scanStatus } = useCompany();
  const auth = useAuth();
  const hasVigilance = auth?.hasVigilance || false;

  const isActive = (path) => location.pathname === path;
  const isSettingsActive = location.pathname.startsWith('/settings');

  // Determine if product nav items should be tier-locked
  const isTierLocked = activeProduct.tierRequired === 'vigilance' && !hasVigilance;

  const isStaging = process.env.REACT_APP_ENV === 'staging';
  const sidebarTop = isStaging ? 'top-[72px] h-[calc(100vh-72px)]' : 'top-topnav h-[calc(100vh-48px)]';

  return (
    <aside className={`fixed left-0 ${sidebarTop} w-sidebar bg-navy-900 border-r border-navy-800 flex flex-col z-40`}>

      {/* Active Company Card */}
      {activeCompany && (
        <div className="px-3 pt-4 pb-2 flex-shrink-0">
          <div className="p-2.5 bg-navy-800 rounded-lg">
            <div className="flex items-center gap-2 mb-0.5">
              <Globe className="w-3.5 h-3.5 text-teal-400" />
              <span className="text-xs font-medium text-white truncate">{activeCompany.name}</span>
            </div>
            <p className="text-xs text-gray-400 truncate ml-5.5">{activeCompany.domain}</p>
            {scanning && scanStatus && (
              <div className="mt-1.5">
                <div className="flex items-center gap-1.5 text-xs text-teal-400">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>{scanStatus.progress}%</span>
                </div>
                <div className="h-1 bg-navy-700 rounded-full mt-1 overflow-hidden">
                  <div
                    className="h-full bg-teal-500 rounded-full transition-all"
                    style={{ width: `${scanStatus.progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── All navigation in one scrollable container (no dividers, natural flow) ── */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4 scrollbar-thin">

        {/* ── MAIN section ── */}
        <SectionLabel>Main</SectionLabel>
        <div className="space-y-0.5">
          {SHELL_MAIN_ITEMS.map(item => (
            <NavLink
              key={item.path}
              item={item}
              active={isActive(item.path)}
            />
          ))}
        </div>

        {/* ── PRODUCT section ── */}
        <SectionLabel color={activeProduct.color}>{activeProduct.name}</SectionLabel>

        {activeProduct.navItems.length > 0 ? (
          <div className="space-y-0.5">
            {activeProduct.navItems.map((item, idx) => {
              // Subsection header (e.g. "Priority", "Analysis")
              if (item.type === 'subsection') {
                return <SubSectionLabel key={`sub-${idx}`}>{item.label}</SubSectionLabel>;
              }
              return (
                <NavLink
                  key={item.path}
                  item={item}
                  active={isActive(item.path)}
                  locked={isTierLocked}
                  activeColor={activeProduct.color}
                />
              );
            })}
          </div>
        ) : (
          /* Coming soon product */
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
              style={{ backgroundColor: activeProduct.color + '20' }}
            >
              {(() => {
                const Icon = ICON_MAP[activeProduct.icon];
                return Icon ? <Icon className="w-6 h-6" style={{ color: activeProduct.color }} /> : null;
              })()}
            </div>
            <p className="text-sm text-gray-400 mb-1">{activeProduct.fullName}</p>
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: activeProduct.color + '15', color: activeProduct.color }}
            >
              Coming Soon
            </span>
          </div>
        )}

        {/* ── SYSTEM section (flows naturally after product items, not pinned to bottom) ── */}
        <SectionLabel>System</SectionLabel>
        <div className="space-y-0.5">
          {SHELL_SYSTEM_ITEMS.map(item => (
            <NavLink
              key={item.path}
              item={item}
              active={
                item.path === '/settings'
                  ? isSettingsActive
                  : isActive(item.path)
              }
            />
          ))}
        </div>
      </nav>
    </aside>
  );
};

export default ProductSidebar;
